import React, { useState, useEffect } from "react";
import { Popover, TextField, Box, Button } from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";

type FilterPopoverProps = {
  column: any;
  setColValue: (cb: (prev: any[]) => any[]) => void;
  iconColor?: string;
};

const DateRangeFilterPopover = ({
  column,
  setColValue,
  iconColor,
}: FilterPopoverProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [dateRange, setDateRange] = useState<{ from_date: string; to_date: string }>({
    from_date: "",
    to_date: "",
  });

  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<Element>) => {
    setAnchorEl(e.currentTarget as HTMLElement);
  };

  const handleClose = () => {
    setAnchorEl(null);
    column.setFilterValue(undefined);
    setColValue((prev: any[]) =>
      (prev || []).filter((f) => f.filterField !== column.id)
    );
    setDateRange({ from_date: "", to_date: "" });
  };

  const handleApply = () => {
    if (dateRange.from_date && dateRange.to_date) {
      // ✅ Directly use dateRange object
      column.setFilterValue(dateRange);

      setColValue((prev: any[]) => {
        const updated = (prev || []).filter(
          (f) => f.filterField !== column.id
        );
        return [...updated, { filterField: column.id, filterValue: dateRange }];
      });
    }
    setAnchorEl(null);
  };

  useEffect(() => {
    const applied = column.getFilterValue();
    if (applied && typeof applied === "object") {
      setDateRange({
        from_date: applied.from_date || "",
        to_date: applied.to_date || "",
      });
    }
  }, [column]);

  return (
    <>
      <FilterAltIcon
        fontSize="small"
        onClick={handleOpen}
        style={{ cursor: "pointer", marginLeft: 6, color: iconColor }}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box p={2} width={250} sx={{ fontFamily: 'Poppins, sans-serif' }}>
          <TextField
            label="From"
            type="date"
            size="small"
            value={dateRange.from_date}
            fullWidth
            InputLabelProps={{ shrink: true }}
            onChange={(e) =>
              setDateRange((prev) => ({ ...prev, from_date: e.target.value }))
            }
            sx={{
              fontFamily: 'Poppins, sans-serif',
              '& input': {
                fontFamily: 'Poppins, sans-serif',
                fontSize: '13px',
              },
              '& .MuiInputLabel-root': {
                fontFamily: 'Poppins, sans-serif',
                fontSize: '13px',
              },
              '& .MuiFormHelperText-root': {
                fontFamily: 'Poppins, sans-serif',
                fontSize: '13px',
              },
            }}
          />
          <Box mt={2}>
            <TextField
              label="To"
              type="date"
              size="small"
              value={dateRange.to_date}
              fullWidth
              InputLabelProps={{ shrink: true }}
              onChange={(e) =>
                setDateRange((prev) => ({ ...prev, to_date: e.target.value }))
              }
              sx={{
                fontFamily: 'Poppins, sans-serif',
                '& input': {
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '13px',
                },
                '& .MuiInputLabel-root': {
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '13px',
                },
                '& .MuiFormHelperText-root': {
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '13px',
                },
              }}
            />
          </Box>

          <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
            <Button
              className="btn btn-secondary"
              size="small"
              variant="outlined"
              onClick={handleClose}
              sx={{ fontFamily: 'Poppins, sans-serif', fontSize: '13px' }}
            >
              Cancel
            </Button>
            <Button
              className="btn btn-primary"
              size="small"
              variant="contained"
              onClick={handleApply}
              disabled={!dateRange.from_date || !dateRange.to_date}
              sx={{ fontFamily: 'Poppins, sans-serif', fontSize: '13px' }}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default DateRangeFilterPopover;
